/*
 * Copyright (c) 2023 - 2026. Bluemate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import User, {UserFields} from '../../../models/userModel';
import {ResponseData, validateEmail} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {RegisterErrors, RegisterStep} from '../../../util/interfaces';
import {AppContext} from '../../misc/appContext';
import {onAddAccount} from '../../../util/events';
import {googleRegister} from '../../../util/requestSender';
import {setSentryUser} from '../../../util/sentryUtil';
import {sendGaEvent, sendGaPageView, signUpEvent} from '../../../util/analytics';
import Register from './register';
import {clearSessionBannerDismissals, saveToAppLocal} from '../../../util/storageUtil';
import {createErrorMessage, getFingerprint} from '../../../util/utils';
import {CredentialResponse} from '@react-oauth/google';
import {fallbackUsersEstimate, getActiveUsersEstimate} from '../../../util/statUtil';
import Invitation from '../../../models/invitationModel';
import {useNavigate} from 'react-router';
import {DASHBOARD_ROUTE} from '../../../util/constants';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const EMPTY_ERRORS: RegisterErrors = {
    password: null,
    lastname: null,
    firstname: null,
    email: null,
    companyName: null
};

export default function RegisterController() {
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const [user, setUser] = useState(new User());
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<RegisterErrors>(EMPTY_ERRORS);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const [invitationID, setInvitationID] = useState<string | null>(null);
    const [step, setStep] = useState<RegisterStep>(RegisterStep.METHOD_CHOICE);
    const [usersEstimate, setUsersEstimate] = useState(fallbackUsersEstimate);
    const navigate = useNavigate();

    function onChangeUser(key: keyof UserFields) {
        return function (value: string) {
            setUser(user.clone().setField(key, value));
        };
    }

    function onChangePassword(password: string) {
        setPassword(password);
    }

    function onToggleTerms() {
        setTermsAccepted(!termsAccepted);
    }

    function onChangeCompanyName(value: string) {
        setCompanyName(value);
    }

    function onResetError(key: keyof RegisterErrors) {
        return function () {
            setErrors(Object.assign({}, errors, {[key]: null}));
        };
    }

    function onClickLogin() {
        sendGaEvent('Click', 'Registreren', 'Log in');
    }

    function onClickGoogle() {
        sendGaEvent('Click', 'Registreren', 'Google registreer');
        signUpEvent('Google');
    }

    function onChangeStep(step: RegisterStep) {
        return function () {
            setStep(step);
        };
    }

    function onSubmit() {
        if (!validateValues()) return;
        sendGaEvent('Click', 'Registreren', 'Registreer');
        signUpEvent('Manual');
        setLoading(true);
        const today = IDate.now();
        user.flex = {
            // We don't need companyName when the user is invited by another user, it also messes with the option
            // to add a seller account in the future
            companyName: invitationID ? undefined : companyName,
            lastNotifiedUpdate: today.format('YYYY-MM-DD'),
            warn_2fa_after: today.format('YYYY-MM-DD')
        };
        user.register(
            password,
            invitationID,
            (user: User, _: boolean, data: ResponseData) => {
                requestCallback(data, user);
            },
            onConnectionError
        );
    }

    // A blocked/failed request (e.g. a Cloudflare 403 after the app sat idle)
    // would otherwise never resolve the spinner. Reset loading and show a
    // retryable error. connectionRecovery may also reload to re-pass the
    // challenge; if it does, this is moot.
    function onConnectionError() {
        setLoading(false);
        showDialog(
            createErrorMessage(
                t('auth.register.connectionError', 'Verbinding mislukt — controleer je internet en probeer opnieuw.')
            ),
            t('auth.register.registrationFailedTitle', 'Registratie mislukt')
        );
    }

    function onClickTrustpilot() {
        window.open('https://www.trustpilot.com/review/bolmate.nl', '_blank', 'noopener,noreferrer');
    }

    function onGoogleSuccess(response: CredentialResponse) {
        const token = response.credential;
        if (!token) return onGoogleError();
        setLoading(true);
        getFingerprint().then((fingerprint: string) => {
            googleRegister({
                data: {token, fp: fingerprint},
                callback: (data: ResponseData) => {
                    requestCallback(data, null);
                },
                errorCallback: onConnectionError
            });
        });
    }

    function onGoogleError() {
        showDialog(
            t('auth.register.googleErrorMessage', 'Er is iets misgegaan bij het registreren met Google.'),
            t('auth.register.googleErrorTitle', 'Registreren mislukt')
        );
    }

    function validateValues(): boolean {
        const {firstname, lastname, email} = user;
        const _errors = Object.assign({}, errors);
        let valid = true;
        if (!firstname.length) {
            _errors.firstname = t('auth.register.firstnameRequired', 'De voornaam mag niet leeg zijn');
            valid = false;
        }
        if (!lastname.length) {
            _errors.lastname = t('auth.register.lastnameRequired', 'De achternaam mag niet leeg zijn');
            valid = false;
        }
        if (!email.length) {
            _errors.email = t('auth.register.emailRequired', 'Het e-mailadres mag niet leeg zijn');
            valid = false;
        } else if (!validateEmail(email)) {
            _errors.email = t('auth.register.emailInvalid', 'Het e-mailadres is niet geldig');
            valid = false;
        }
        if (!password.length) {
            _errors.password = t('auth.register.passwordRequired', 'Het wachtwoord mag niet leeg zijn');
            valid = false;
        }
        if (!companyName.length) {
            _errors.companyName = t('auth.register.companyNameRequired', 'De bedrijfsnaam mag niet leeg zijn');
            valid = false;
        }
        if (!termsAccepted) {
            showDialog(t('auth.register.acceptTerms', 'Accepteer de voorwaarden om een account te registreren'));
            valid = false;
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    function requestCallback(data: ResponseData, user: User | null) {
        setLoading(false);
        if (data.success) {
            saveToAppLocal('newUser', true);
            clearSessionBannerDismissals();
            if (!user) user = new User(data.user);
            setSentryUser(user);
            const account = user.accounts.length ? user.accounts[0] : undefined;
            context.onChangeContext({user, accounts: user.accounts, account}, () => {
                if (account) navigate(DASHBOARD_ROUTE);
                else onAddAccount();
            });
        } else {
            // The backend always responds in Dutch — map known messages onto i18n keys
            const message =
                data.message === 'Het opgegeven e-mailadres is al in gebruik.'
                    ? t('auth.register.emailInUse', 'Het opgegeven e-mailadres is al in gebruik.')
                    : data.message;
            showDialog(
                createErrorMessage(
                    message ?? t('auth.register.createAccountFailed', 'Het aanmaken van het account is niet gelukt.')
                ),
                t('auth.register.registrationFailedTitle', 'Registratie mislukt')
            );
        }
    }

    function loadUsersEstimate() {
        getActiveUsersEstimate().then((estimate: number) => {
            setUsersEstimate(estimate);
        });
    }

    function loadInvitation() {
        const params = new URLSearchParams(location.search);
        if (!params.has('uitnodiging')) return;
        setLoading(true);
        const _invitationID = params.get('uitnodiging')!;
        Invitation.check(_invitationID, (invitation: Invitation | null, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                setInvitationID(invitation!.id);
                setUser(user.clone().setField('email', invitation!.email));
                setCompanyName(data.company_name);
                setStep(RegisterStep.FORM);
            } else
                showDialog(
                    data.message ??
                        createErrorMessage(
                            t('auth.register.invitationNotFound', 'De uitnodiging kan niet gevonden worden.')
                        ),
                    t('auth.register.invitationFailedTitle', 'Uitnodiging mislukt'),
                    [{label: t('auth.register.okButton', 'Oké'), onClick: window.close}]
                );
        });
    }

    useEffect(() => {
        sendGaPageView('/registreren');
        loadUsersEstimate();
        loadInvitation();
    }, []);

    return (
        <Register
            onChangeUser={onChangeUser}
            onChangePassword={onChangePassword}
            onChangeTerms={onToggleTerms}
            onChangeCompanyName={onChangeCompanyName}
            onSubmit={onSubmit}
            onResetError={onResetError}
            onGoogleSuccess={onGoogleSuccess}
            onGoogleError={onGoogleError}
            onClickLogin={onClickLogin}
            onClickGoogle={onClickGoogle}
            user={user}
            password={password}
            loading={loading}
            errors={errors}
            termsAccepted={termsAccepted}
            companyName={companyName}
            step={step}
            onChangeStep={onChangeStep}
            onClickTrustPilot={onClickTrustpilot}
            usersEstimate={usersEstimate}
            isInvited={!!invitationID}
        />
    );
}
